import { forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Popover, Dropdown } from 'rsuite';
import useMediaQuery from '@mui/material/useMediaQuery';
import { fetchAllGroups } from '@app/store/slices/myTeamSlice';
import { icons } from '@shared/static/icons';
import { roles } from '@shared/static/roles';
import {
  useViewChartDialog,
  useViewMemberChartDialog,
  useExportDialog,
  useFreezeTeamDialog,
  useDeleteTeamDialog,
  useCreateTeamDialog,
  useEditMemberDialog,
  useFreezeMemberDialog,
  useMoveMemberDialog,
  useUnfreezeTeamDialog,
  useUnfreezeMemberDialog,
  useNewMemberDialog,
  useChangeRoleDialog,
  useDeleteMemberDialog,
} from '@utils/dialogs-hooks';
import { hasActiveTeamLead } from '@utils/helpers';
import { theme } from '@utils/libs/theme';

/**
 * {@link TeamActionCellMenu} => Teams table action cell (Desktop)
 * {@link EditTeamMenu} => /teams/:id edit team menu (Desktop, Mobile)
 * {@link MemberActionCellMenu} => MembersTable action cell (Desktop)
 * {@link TeamTableMobileMenu} => TeamsTable mobile menu (Mobile)
 * {@link TeamMemberMobileMenu} => MembersTable mobile menu (Mobile)
 */

export const TeamActionCellMenu = forwardRef(
  ({ close, data, ...rest }, ref) => {
    const { t } = useTranslation();
    const { openExportDialog } = useExportDialog();
    const { openFreezeTeamDialog } = useFreezeTeamDialog();
    const { openUnfreezeTeamDialog } = useUnfreezeTeamDialog();
    const { openDeleteTeamDialog } = useDeleteTeamDialog();
    const { openCreateTeamDialog } = useCreateTeamDialog();
    const { user } = useSelector((state) => state.user);

    return (
      <Popover ref={ref} {...rest} full onClick={(e) => e.stopPropagation()}>
        <Dropdown.Menu onSelect={close} className="dropdown-menu">
          {!user?.group_role && (
            <Dropdown.Item
              onSelect={() => openCreateTeamDialog(data)}
              icon={icons.edit}
            >
              {t('edit')}
            </Dropdown.Item>
          )}
          {/* <Dropdown.Item
            onSelect={() => openExportDialog('Export expenses for team', data)}
            icon={icons.refresh}
          >
            {t('export')}
          </Dropdown.Item> */}
          {!user?.group_role && <Dropdown.Separator />}
          {!data.is_frozen && !user?.group_role && (
            <Dropdown.Item
              className="freeze-color"
              onSelect={() => openFreezeTeamDialog(data)}
              icon={icons.freeze}
            >
              {t('freeze')}
            </Dropdown.Item>
          )}
          {data.is_frozen && !user?.group_role && (
            <Dropdown.Item
              onSelect={() => openUnfreezeTeamDialog(data)}
              icon={icons.unfreeze}
            >
              {t('unfreeze')}
            </Dropdown.Item>
          )}
          {!user?.group_role && (
            <Dropdown.Item
              icon={icons.delete}
              className="delete-color"
              onSelect={() => openDeleteTeamDialog(data)}
            >
              {t('delete')}
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Popover>
    );
  },
);
TeamActionCellMenu.displayName = 'TeamActionCellMenu';

export const EditTeamMenu = forwardRef(({ close, data, ...rest }, ref) => {
  const { t } = useTranslation();
  const { openViewChartDialog } = useViewChartDialog();
  const { openExportDialog } = useExportDialog();
  const { openFreezeTeamDialog } = useFreezeTeamDialog();
  const { openUnfreezeTeamDialog } = useUnfreezeTeamDialog();
  const { openDeleteTeamDialog } = useDeleteTeamDialog();
  const { openCreateTeamDialog } = useCreateTeamDialog();
  const { openNewMemberDialog } = useNewMemberDialog();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useSelector((state) => state.user);

  return (
    <Popover ref={ref} {...rest} full onClick={(e) => e.stopPropagation()}>
      <Dropdown.Menu onSelect={close} className="dropdown-menu">
        {isMobile && (
          <Dropdown.Item
            icon={icons.profile}
            onSelect={() => openNewMemberDialog(data)}
          >
            {t('invite_member')}
          </Dropdown.Item>
        )}
        {!user?.group_role && (
          <>
            {isMobile && (
              <>
                <Dropdown.Item
                  icon={icons.edit}
                  onSelect={() => openCreateTeamDialog(data)}
                >
                  {t('edit_team')}
                </Dropdown.Item>

                <Dropdown.Separator />
              </>
            )}
            <Dropdown.Item
              icon={icons.chart}
              onSelect={() => openViewChartDialog(data)}
            >
              {t('view_chart')}
            </Dropdown.Item>
            <Dropdown.Item
              onSelect={() =>
                openExportDialog('Export expenses for team', data)
              }
              icon={icons.refresh}
            >
              {t('export')}
            </Dropdown.Item>
            <Dropdown.Separator />
            {!data?.is_frozen && (
              <Dropdown.Item
                className="freeze-color"
                onSelect={() => openFreezeTeamDialog(data)}
                icon={icons.freeze}
              >
                {t('freeze')}
              </Dropdown.Item>
            )}
            {data?.is_frozen && (
              <Dropdown.Item
                onSelect={() => openUnfreezeTeamDialog(data)}
                icon={icons.unfreeze}
              >
                {t('unfreeze')}
              </Dropdown.Item>
            )}
            <Dropdown.Item
              icon={icons.delete}
              className="delete-color"
              onSelect={() => openDeleteTeamDialog(data)}
            >
              {t('delete')}
            </Dropdown.Item>
          </>
        )}
      </Dropdown.Menu>
    </Popover>
  );
});
EditTeamMenu.displayName = 'EditTeamMenu';

export const MemberActionCellMenu = forwardRef(
  ({ close, data, ...rest }, ref) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { openEditMemberDialog } = useEditMemberDialog();
    const { openFreezeMemberDialog } = useFreezeMemberDialog();
    const { openUnfreezeMemberDialog } = useUnfreezeMemberDialog();
    const { openMoveMemberDialog } = useMoveMemberDialog();
    const { openChangeRoleDialog } = useChangeRoleDialog();
    const { openDeleteMemberDialog } = useDeleteMemberDialog();
    const { openViewMemberChartDialog } = useViewMemberChartDialog();

    const { user } = useSelector((state) => state.user);
    const { allGroups } = useSelector((state) => state.my_team);
    const dispatch = useDispatch();

    useEffect(() => {
      dispatch(fetchAllGroups());
    }, [dispatch]);

    const alreadyHasTeamLead = hasActiveTeamLead(data, allGroups);

    const isTeamLeadSelf =
      data.group_role === roles.TEAM_LEAD && data.id === user?.id;

    const isTeamLeadWithoutGroup =
      data.group_role === roles.TEAM_LEAD && data.groups?.length === 0;

    return (
      <Popover ref={ref} {...rest} full onClick={(e) => e.stopPropagation()}>
        <Dropdown.Menu onSelect={close} className="dropdown-menu">
          {!isMobile && (
            <Dropdown.Item
              onSelect={() => openEditMemberDialog(data)}
              icon={icons.edit}
            >
              {t('edit')}
            </Dropdown.Item>
          )}
          {isMobile && !isTeamLeadWithoutGroup && (
            <Dropdown.Item
              onSelect={() => openChangeRoleDialog(data, alreadyHasTeamLead)}
              icon={icons.edit}
            >
              {t('change_role')}
            </Dropdown.Item>
          )}
          <Dropdown.Item
            icon={icons.chart}
            onSelect={() => openViewMemberChartDialog(data)}
          >
            {t('view_chart')}
          </Dropdown.Item>
          {!isTeamLeadSelf && !isTeamLeadWithoutGroup && <Dropdown.Separator />}
          {!isTeamLeadSelf &&
            (data.is_frozen ? (
              <Dropdown.Item
                onSelect={() => openUnfreezeMemberDialog(data)}
                icon={icons.unfreeze}
              >
                {t('unfreeze')}
              </Dropdown.Item>
            ) : (
              <Dropdown.Item
                className="freeze-color"
                onSelect={() => openFreezeMemberDialog(data)}
                icon={icons.freeze}
              >
                {t('freeze')}
              </Dropdown.Item>
            ))}
          {data.group_role === roles.TEAM_MEMBER &&
            !user?.group_role &&
            !isMobile && (
              <Dropdown.Item
                icon={icons.transfer}
                onSelect={() => openMoveMemberDialog(data)}
              >
                {t('move_to_other_team')}
              </Dropdown.Item>
            )}
          {isMobile && (
            <Dropdown.Item
              icon={icons.delete}
              className="delete-color"
              onSelect={() => openDeleteMemberDialog(data)}
            >
              {t('delete')}
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Popover>
    );
  },
);
MemberActionCellMenu.displayName = 'MemberActionCellMenu';

export const TeamTableMobileMenu = forwardRef(
  ({ close, data, ...rest }, ref) => {
    const { t } = useTranslation();
    const { openCreateTeamDialog } = useCreateTeamDialog();
    const { openNewMemberDialog } = useNewMemberDialog();
    const { user } = useSelector((state) => state.user);

    return (
      <Popover ref={ref} {...rest} full onClick={(e) => e.stopPropagation()}>
        <Dropdown.Menu onSelect={close} className="dropdown-menu">
          {!user?.group_role && (
            <Dropdown.Item
              icon={icons.add}
              onSelect={() => openCreateTeamDialog(data)}
            >
              {t('create_team')}
            </Dropdown.Item>
          )}
          <Dropdown.Item
            icon={icons.profile}
            onSelect={() => openNewMemberDialog(data)}
          >
            {t('invite_member')}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Popover>
    );
  },
);
TeamTableMobileMenu.displayName = 'TeamTableMobileMenu';

export const TeamMemberMobileMenu = forwardRef(
  ({ close, data, ...rest }, ref) => {
    const { t } = useTranslation();
    const { openFreezeMemberDialog } = useFreezeMemberDialog();
    const { openUnfreezeMemberDialog } = useUnfreezeMemberDialog();
    const { openViewMemberChartDialog } = useViewMemberChartDialog();
    const { user } = useSelector((state) => state.user);

    const isTeamLeadSelf =
      data.group_role === roles.TEAM_LEAD && data.id === user?.id;

    return (
      <Popover ref={ref} {...rest} full onClick={(e) => e.stopPropagation()}>
        <Dropdown.Menu onSelect={close} className="dropdown-menu">
          <Dropdown.Item
            icon={icons.chart}
            onSelect={() => openViewMemberChartDialog(data)}
          >
            {t('view_chart')}
          </Dropdown.Item>
          {!isTeamLeadSelf &&
            (data.is_frozen ? (
              <Dropdown.Item
                onSelect={() => openUnfreezeMemberDialog(data)}
                icon={icons.unfreeze}
              >
                {t('unfreeze')}
              </Dropdown.Item>
            ) : (
              <Dropdown.Item
                className="freeze-color"
                onSelect={() => openFreezeMemberDialog(data)}
                icon={icons.freeze}
              >
                {t('freeze')}
              </Dropdown.Item>
            ))}
        </Dropdown.Menu>
      </Popover>
    );
  },
);
TeamMemberMobileMenu.displayName = 'TeamMemberMobileMenu';
